import { CHANNEL_NAMES } from './agentReadiness';

// Inicio del admin: convierte el resumen de public.admin_overview() (script 43) en lo que importa, en lenguaje claro y con a dónde ir.
// Cada pista dice QUÉ pasa, DÓNDE y trae el enlace a la pantalla que lo arregla. Nada aquí se inventa: si el resumen no trae algo, no se dice.

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
const names = (list, max = 3) => {
  const n = (list || []).map((a) => `«${a.name}»`);
  return n.length > max ? `${n.slice(0, max).join(', ')} y ${n.length - max} más` : n.join(', ');
};
const clip = (s, n = 140) => { const t = String(s || '').replace(/\s+/g, ' ').trim(); return t.length > n ? `${t.slice(0, n)}…` : t; };

const RANK = { error: 0, warn: 1, info: 2 };

/** @returns {{ id: string, level: 'error'|'warn'|'info', title: string, detail?: string, to?: string, action?: string }[]} del más grave al menos grave. */
export function buildAttention(o) {
  if (!o) return [];
  const out = [];

  for (const c of o.credentials_bad || []) {
    const first = (c.agents || [])[0];
    out.push({
      id: `cred-${c.id}`, level: 'error',
      title: `La clave «${c.label}» no funciona`,
      detail: `${c.status === 'revoked' ? 'Está revocada.' : 'OpenAI la rechazó.'} La ${c.agent_count === 1 ? 'usa' : 'usan'} ${plural(c.agent_count, 'agente', 'agentes')}: ${names(c.agents)}.${c.last_error ? ` ${clip(c.last_error, 100).replace(/[.\s]+$/, '')}.` : ''} Si caducó, edítala y pega la nueva.`,
      ...(first ? { to: `/agentes/${first.id}/clave`, action: 'Editar la clave' } : {}),
    });
  }

  if ((o.queue?.stuck || 0) > 0) {
    out.push({ id: 'queue-stuck', level: 'error', title: `${plural(o.queue.stuck, 'mensaje lleva', 'mensajes llevan')} más de 10 minutos sin procesarse`,
      detail: 'El motor podría estar detenido o saturado. Revisa que esté arriba en el servidor.' });
  }

  for (const k of o.channels_bad || []) {
    const ch = CHANNEL_NAMES[k.channel] || k.channel;
    out.push({
      id: `chan-${k.id}`, level: k.status === 'error' ? 'error' : 'warn',
      title: `${ch} de «${k.agent_name}»${k.status === 'error' ? ' tiene un error' : ' está fallando'}`,
      detail: k.last_error ? clip(k.last_error) : 'Meta o Telegram no están respondiendo bien a este canal.',
      to: `/agentes/${k.agent_id}/canales`, action: 'Ver el canal',
    });
  }

  for (const a of o.agents_with_errors || []) {
    const bad = a.total > 0 && a.errors / a.total >= 0.5;
    out.push({
      id: `err-${a.agent_id}`, level: bad ? 'error' : 'warn',
      title: `«${a.name}» falló ${a.errors} de ${plural(a.total, 'mensaje', 'mensajes')} en 24 horas`,
      detail: a.last_reason ? `Último error: ${clip(a.last_reason)}` : undefined,
      to: `/agentes/${a.agent_id}/actividad`, action: 'Ver la actividad',
    });
  }

  for (const b of o.whatsapp_billing || []) {
    out.push({
      id: `bill-${b.id}`, level: 'warn',
      title: b.state === 'problem' ? `WhatsApp de «${b.agent_name}» tiene un problema con el método de pago` : `WhatsApp de «${b.agent_name}» no tiene método de pago en Meta`,
      detail: `${b.number ? `${b.number}. ` : ''}Según la última revisión. Sin él, Meta puede limitar o bloquear el envío de mensajes.`,
      to: `/agentes/${b.agent_id}/canales`, action: 'Revisar',
    });
  }

  if ((o.queue?.failed_24h || 0) > 0) {
    out.push({ id: 'queue-failed', level: 'warn', title: `${plural(o.queue.failed_24h, 'mensaje no se pudo procesar', 'mensajes no se pudieron procesar')} en 24 horas`,
      detail: 'Se reintentaron y no se logró. Mira la actividad del agente para ver qué los detuvo.' });
  }

  if ((o.no_cost?.messages || 0) > 0) {
    const models = (o.no_cost.models || []).slice(0, 3).join(', ');
    out.push({ id: 'no-cost', level: 'warn', title: `${plural(o.no_cost.messages, 'respuesta', 'respuestas')} de los últimos 7 días sin costo`,
      detail: `Falta el precio${models ? ` de ${models}` : ' de un modelo'}: su gasto no se está sumando.`, to: '/admin-precios', action: 'Poner el precio' });
  }

  if ((o.paused || 0) > 0) {
    out.push({ id: 'paused', level: 'info', title: `${plural(o.paused, 'conversación espera', 'conversaciones esperan')} a una persona`,
      detail: 'El agente la pasó a alguien del equipo. Si nadie responde a tiempo, el agente vuelve a atenderla.' });
  }

  return out.sort((a, b) => RANK[a.level] - RANK[b.level]);
}

/** La frase de arriba: qué tan bien va todo, sin adornos. */
export function statusLine(items, o) {
  if (!o) return 'Revisando la plataforma…';
  const n = items.filter((i) => i.level !== 'info').length;
  if (n > 0) return n === 1 ? 'Hay 1 cosa que necesita tu atención.' : `Hay ${n} cosas que necesitan tu atención.`;
  const a = o.agents || {};
  if (a.platform_responding > 0) return `Todo en orden: ${plural(a.platform_responding, 'agente responde', 'agentes responden')} con el motor propio.`;
  return 'Todo en orden.';
}

/** Tiempo en segundos con un decimal ("2,5 s"), o rayita si no hay dato. */
export const seconds = (ms) => (ms == null ? '—' : `${(ms / 1000).toLocaleString('es-MX', { maximumFractionDigits: 1 })} s`);

/** % de mensajes de las 24 h que el motor respondió; null si no hubo mensajes. */
export const answeredPct = (e) => (!e || !e.total ? null : Math.round((e.ok / e.total) * 100));

/** Cuántos agentes son de la plataforma y cuántos externos (dónde viven), como un dato. */
export function originSplit(a) {
  const platform = a?.platform || 0;
  const external = (a?.external_n8n || 0) + (a?.external_web || 0) + (a?.external_other || 0);
  const total = platform + external;
  return { platform, external, total, platformPct: total ? Math.round((platform / total) * 100) : 0,
    where: [['n8n', a?.external_n8n || 0], ['página web', a?.external_web || 0], ['otro sistema', a?.external_other || 0]].filter(([, n]) => n > 0) };
}
